<template>
  <Dialog
    v-model:visible="visible"
    :header="isEditMode ? $t('g.editShare') : $t('g.publishShare')"
    modal
    :style="{ width: '600px' }"
    class="share-edit-dialog"
  >
    <div class="p-fluid">
      <div class="field">
        <label for="share-title" class="font-semibold text-gray-700 mt-2 block">
          {{ $t('g.title') }}
        </label>
        <InputText
          id="share-title"
          v-model="formData.title"
          :placeholder="$t('g.enterTitle')"
          class="w-full"
        />
      </div>

      <div class="field">
        <label
          for="share-description"
          class="font-semibold text-gray-700 mt-2 block"
        >
          {{ $t('g.description') }}
        </label>
        <Textarea
          id="share-description"
          v-model="formData.description"
          :placeholder="$t('g.enterDescription')"
          rows="4"
          class="w-full"
          auto-resize
        />
      </div>

      <div class="field mt-4">
        <label for="share-tags" class="font-semibold text-gray-700 mt-2 block">
          {{ $t('g.tags') }}
        </label>
        <MultiSelect
          v-if="Array.isArray(availableTags)"
          id="share-tags"
          v-model="formData.tag_ids"
          :options="availableTags"
          option-label="name"
          option-value="id"
          :placeholder="$t('g.selectTags')"
          class="w-full"
          :loading="loadingTags"
          :filter="true"
          :show-clear="true"
          display="chip"
          style="min-height: 42px"
        />
        <div v-else class="p-3 text-gray-500">{{ $t('g.loading') }}...</div>
      </div>

      <div class="field mt-4">
        <label class="font-semibold text-gray-700 mt-2 block">
          {{ $t('g.privacy') }}
        </label>
        <div class="flex gap-4">
          <div class="flex items-center">
            <RadioButton
              id="public"
              v-model="formData.is_public"
              :value="true"
              name="privacy"
            />
            <label for="public" class="ml-2">{{ $t('g.public') }}</label>
          </div>
          <div class="flex items-center">
            <RadioButton
              id="private"
              v-model="formData.is_public"
              :value="false"
              name="privacy"
            />
            <label for="private" class="ml-2">{{ $t('g.private') }}</label>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <Button
          :label="$t('g.cancel')"
          icon="pi pi-times"
          severity="secondary"
          text
          @click="$emit('cancel')"
        />
        <Button
          :label="isEditMode ? $t('g.update') : $t('g.publish')"
          :icon="isEditMode ? 'pi pi-check' : 'pi pi-share-alt'"
          :loading="loading"
          :disabled="loading"
          @click="$emit('submit', formData)"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import MultiSelect from 'primevue/multiselect'
import RadioButton from 'primevue/radiobutton'
import Textarea from 'primevue/textarea'
import { computed, ref, watch } from 'vue'

import { TagService } from '@/services/tagService'
import type { TagResponse } from '@/services/tagService'

interface Props {
  visible: boolean
  isEditMode?: boolean
  loading?: boolean
  initialData?: {
    title: string
    description: string
    tag_ids: number[]
    is_public: boolean
  }
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'cancel'): void
  (e: 'submit', data: any): void
}

const props = withDefaults(defineProps<Props>(), {
  isEditMode: false,
  loading: false,
  initialData: () => ({
    title: '',
    description: '',
    tag_ids: [],
    is_public: true
  })
})

const emit = defineEmits<Emits>()

// 表单数据
const formData = ref({
  title: '',
  description: '',
  tag_ids: [] as number[],
  is_public: true
})

// 标签相关
const availableTags = ref<TagResponse[]>([])
const loadingTags = ref(false)

// 监听 visible 变化，初始化表单数据
watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      // 确保 tag_ids 始终是数组
      const tagIds = props.initialData?.tag_ids
      formData.value = {
        title: props.initialData?.title || '',
        description: props.initialData?.description || '',
        tag_ids: Array.isArray(tagIds) ? tagIds : [],
        is_public: props.initialData?.is_public !== false
      }
      void loadAvailableTags()
    }
  }
)

// 加载可用标签
const loadAvailableTags = async () => {
  try {
    loadingTags.value = true
    const tags = await TagService.getActiveTags()
    availableTags.value = Array.isArray(tags) ? tags : []
  } catch (error) {
    console.error('加载标签失败:', error)
    availableTags.value = [] // 确保出错时也是空数组
  } finally {
    loadingTags.value = false
  }
}

// 计算属性：visible
const visible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})
</script>
